import React from 'react';
import {
  Button,
  Checkbox,
  DateRangePicker,
  Input,
  Table,
  TagPicker,
  Tooltip,
  Whisper,
} from 'rsuite';
import * as requestsApi from '../../api/requests';
import * as coursesApi from '../../api/courses';
import * as studentsApi from '../../api/students';
import EditIcon from '@rsuite/icons/Edit';
import PlusIcon from '@rsuite/icons/Plus';
import EditRequestModal from './EditRequestModal';
import DeleteIcon from '@rsuite/icons/Trash';
import RequestQueuesModal from './RequestQueuesModal';
import EditStudentModal from '../students/EditStudentModal';
import { DateRange } from 'rsuite/DateRangePicker';
import PageLayout from '../../common/PageLayout';
import { formatDateTime, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useTableColumns } from '../../common/useTableColumns';
import { useResourceList } from '../../common/useResourceList';
import { useConfirm } from '../../common/useConfirm';
import StatusBadge from '../../common/StatusBadge';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import { truncatedTextCell } from '../../common/tableCells';

const { Column, HeaderCell, Cell } = Table;

const REQUESTS_TABLE_COLUMNS_STORAGE_KEY = 'requests:tableColumns:v2';
const LEGACY_REQUESTS_TABLE_COLUMNS_STORAGE_KEYS = [
  'requests:tableColumns',
  'tableColumns',
];
const COURSES_PREVIEW_COUNT = 2;
const DEFAULT_REQUESTS_DATE_RANGE_YEARS = 1;

const getDefaultRequestsDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - DEFAULT_REQUESTS_DATE_RANGE_YEARS);
  return [start, end];
};

type RequestsTableColumn = {
  value: string;
  label: string;
  display: boolean;
  width: number;
  fixed?: boolean | 'left' | 'right';
  fullText?: boolean;
  align?: 'left' | 'center';
};

const TABLE_COLUMNS: RequestsTableColumn[] = [
  {
    value: 'email',
    label: 'Пошта',
    display: true,
    width: 260,
    fixed: true,
    fullText: true,
    align: 'left',
  },
  {
    value: 'type',
    label: 'Тип',
    display: true,
    width: 120,
    fullText: true,
  },
  {
    value: 'status',
    label: 'Статус',
    display: true,
    width: 156,
  },
  {
    value: 'secondName',
    label: 'Прізвище',
    display: true,
    width: 170,
    fullText: true,
    align: 'left',
  },
  {
    value: 'firstName',
    label: "Ім'я",
    display: true,
    width: 150,
    fullText: true,
    align: 'left',
  },
  {
    value: 'childFullName',
    label: 'ПІБ дитини',
    display: true,
    width: 240,
    fullText: true,
    align: 'left',
  },
  {
    value: 'phone',
    label: 'Телефон',
    display: true,
    width: 150,
    fullText: true,
  },
  { value: 'age', label: 'Вік', display: true, width: 72 },
  {
    value: 'course',
    label: 'Курс',
    display: false,
    width: 260,
    fullText: true,
    align: 'left',
  },
  {
    value: 'courses',
    label: 'Курси',
    display: true,
    width: 280,
    align: 'left',
  },
  {
    value: 'data',
    label: 'Коментарі',
    display: true,
    width: 280,
    align: 'left',
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    width: 160,
    fullText: true,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    width: 160,
    fullText: true,
  },
];

// One-time migration: copy any legacy storage key into the canonical key
// so `useTableColumns` reads the same data going forward. Runs at module
// load — before any component effect.
if (typeof localStorage !== 'undefined') {
  if (!localStorage.getItem(REQUESTS_TABLE_COLUMNS_STORAGE_KEY)) {
    for (const key of LEGACY_REQUESTS_TABLE_COLUMNS_STORAGE_KEYS) {
      const legacy = localStorage.getItem(key);
      if (legacy) {
        localStorage.setItem(REQUESTS_TABLE_COLUMNS_STORAGE_KEY, legacy);
        break;
      }
    }
  }
}

const normalizeCellText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return String(value).trim();
};

const renderCoursesSummary = (courses: Array<{ name?: string }> = []) => {
  const names = courses
    .map(course => normalizeCellText(course?.name))
    .filter(Boolean);

  if (!names.length) {
    return <span className="requests-table__cell-text">-</span>;
  }

  const full = names.join(', ');
  const shortBase = names.slice(0, COURSES_PREVIEW_COUNT).join(', ');
  const remaining = names.length - COURSES_PREVIEW_COUNT;
  const short = remaining > 0 ? `${shortBase} +${remaining}` : shortBase;

  if (short === full) {
    return <span className="requests-table__cell-text">{short}</span>;
  }

  return (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={<Tooltip>{full}</Tooltip>}
    >
      <span className="requests-table__cell-text">{short}</span>
    </Whisper>
  );
};

const statusBadgeTone = (
  name: string
): 'info' | 'warning' | 'success' | 'danger' | 'neutral' => {
  switch (name) {
    case 'Створена':
      return 'info';
    case 'Перевіряється':
      return 'warning';
    case 'Підтверджена':
    case 'Зарахована':
      return 'success';
    case 'Відхилена':
      return 'danger';
    default:
      return 'neutral';
  }
};

type RequestRow = {
  id: string;
  email: string;
  type: number;
  typeName: string;
  status: number;
  statusName: string;
  firstName: string;
  secondName: string;
  childFullName: string;
  phone: string;
  age: number;
  course: string;
  courses: Array<{ name?: string }>;
  data: string;
  student: unknown;
  createdAt: number;
  updatedAt: number;
};

export default function Requests() {
  const [confirm, ConfirmDialog] = useConfirm();
  const clickedCellRef = React.useRef(false);
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [secondName, setSecondName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [course, setCourse] = React.useState('');
  const [typesData, setTypesData] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [studentsTypesData, setStudentsTypesData] = React.useState<
    { value: number; label: string }[]
  >([]);
  const [types, setTypes] = React.useState<string[]>([]);
  const [statusesData, setStatusesData] = React.useState<
    { value: number; label: string }[]
  >([]);
  const [statuses, setStatuses] = React.useState<string[]>([]);
  const [createdAtFilter, setCreatedAtFilter] = React.useState<DateRange>(() =>
    getDefaultRequestsDateRange()
  );
  const [updatedAtFilter, setUpdatedAtFilter] = React.useState<DateRange>(() =>
    getDefaultRequestsDateRange()
  );
  const [coursesData, setCoursesData] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [courseIds, setCourseIds] = React.useState<string[]>([]);
  const [isStudent, setIsStudent] = React.useState<boolean>(false);
  const [addToQueueRequestId, setAddToQueueRequestId] =
    React.useState<string>('');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    REQUESTS_TABLE_COLUMNS_STORAGE_KEY
  );

  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [studentModalObject, setStudentModalObject] = React.useState<
    any | null
  >(null);

  const list = useResourceList<RequestRow>({
    fetch: ({ page, perPage, sortColumn, sortOrder }) => {
      const filterCreatedAt =
        createdAtFilter[0].getTime() === 0
          ? undefined
          : createdAtFilter.map(d => d.getTime());
      const filterUpdatedAt =
        updatedAtFilter[0].getTime() === 0
          ? undefined
          : updatedAtFilter.map(d => d.getTime());
      return requestsApi.list(
        page,
        perPage,
        email || undefined,
        name,
        secondName,
        phone,
        types,
        statuses,
        course,
        courseIds,
        isStudent,
        sortColumn,
        sortOrder,
        filterCreatedAt,
        filterUpdatedAt
      );
    },
    filters: [
      email,
      name,
      secondName,
      phone,
      course,
      types,
      statuses,
      courseIds,
      isStudent,
      createdAtFilter,
      updatedAtFilter,
    ],
    initialSortColumn: 'createdAt',
  });

  const getTagsPickersData = async () => {
    const { types: studentsResponse } = await studentsApi.getTypes();
    const { types: typesResponse } = await requestsApi.getTypes();
    const { statuses: statusesResponse } = await requestsApi.getStatuses();
    const { courses: coursesResponse } = await coursesApi.list(
      'updatedAt',
      'DESC'
    );
    setStudentsTypesData(studentsResponse);
    setTypesData(typesResponse);
    setStatusesData(statusesResponse);
    setCoursesData(
      coursesResponse.map((c: any) => ({ value: c.id, label: c.name }))
    );
  };

  React.useEffect(() => {
    getTagsPickersData().catch(console.error);
    // Intentionally one-time setup; `getTagsPickersData` is module-local
    // and reads no closed-over state, so no deps are needed.
  }, []);

  const rowHeight = 52;
  const headerHeight = 54;
  const getColumnCellClassName = (column: RequestsTableColumn): string => {
    const classes = [];

    if (column.align === 'left') {
      classes.push('requests-cell-left');
    }

    if (
      ['email', 'secondName', 'firstName', 'childFullName', 'course'].includes(
        column.value
      )
    ) {
      classes.push('requests-cell-ellipsis', 'requests-cell-nowrap');
    }

    if (
      ['phone', 'age', 'type', 'createdAt', 'updatedAt'].includes(column.value)
    ) {
      classes.push('requests-cell-nowrap');
    }

    if (column.value === 'email') {
      classes.push('table-cell-primary');
    }

    return classes.join(' ');
  };

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  // Email is the always-visible search; every other dimension is a filter.
  // Date ranges default to "last year" and are excluded from the count.
  const activeFilterCount =
    [name, secondName, phone, course].filter(Boolean).length +
    [types, statuses, courseIds].filter(arr => arr.length > 0).length +
    (isStudent ? 1 : 0);

  const resetFilters = () => {
    setName('');
    setSecondName('');
    setPhone('');
    setCourse('');
    setTypes([]);
    setStatuses([]);
    setCourseIds([]);
    setIsStudent(false);
    setCreatedAtFilter(getDefaultRequestsDateRange());
    setUpdatedAtFilter(getDefaultRequestsDateRange());
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Анкети"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за поштою"
              value={email || ''}
              onChange={setEmail}
            />
            <FilterPanel activeCount={activeFilterCount} onReset={resetFilters}>
              {filterContainer => (
                <>
                  <FilterField label="Ім'я">
                    <Input
                      placeholder="Ім'я"
                      value={name || ''}
                      onChange={setName}
                    />
                  </FilterField>
                  <FilterField label="Прізвище">
                    <Input
                      placeholder="Прізвище"
                      value={secondName || ''}
                      onChange={setSecondName}
                    />
                  </FilterField>
                  <FilterField label="Телефон">
                    <Input
                      placeholder="Телефон"
                      value={phone || ''}
                      onChange={setPhone}
                    />
                  </FilterField>
                  <FilterField label="Курс (текст)">
                    <Input
                      placeholder="Назва курсу"
                      value={course || ''}
                      onChange={setCourse}
                    />
                  </FilterField>
                  <FilterField label="Тип">
                    <TagPicker
                      block
                      placeholder="Оберіть тип"
                      value={types}
                      onChange={setTypes}
                      data={typesData}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                  <FilterField label="Статус">
                    <TagPicker
                      block
                      placeholder="Оберіть статус"
                      value={statuses}
                      onChange={setStatuses}
                      data={statusesData}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                  <FilterField label="Курси (список)">
                    <TagPicker
                      block
                      placeholder="Курси"
                      value={courseIds}
                      onChange={setCourseIds}
                      data={coursesData}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                  <FilterField
                    label="Створено"
                    hint="За замовчуванням показано останній рік"
                  >
                    <DateRangePicker
                      block
                      showOneCalendar
                      format="dd.MM.yyyy HH:mm"
                      container={filterContainer}
                      preventOverflow
                      onClean={() => {
                        setCreatedAtFilter(getDefaultRequestsDateRange());
                      }}
                      value={createdAtFilter}
                      onChange={(value: DateRange | null) => {
                        if (value) {
                          setCreatedAtFilter([value[0], value[1]]);
                        }
                      }}
                    />
                  </FilterField>
                  <FilterField
                    label="Оновлено"
                    hint="За замовчуванням показано останній рік"
                  >
                    <DateRangePicker
                      block
                      showOneCalendar
                      format="dd.MM.yyyy HH:mm"
                      container={filterContainer}
                      preventOverflow
                      onClean={() => {
                        setUpdatedAtFilter(getDefaultRequestsDateRange());
                      }}
                      value={updatedAtFilter}
                      onChange={(value: DateRange | null) => {
                        if (value) {
                          setUpdatedAtFilter([value[0], value[1]]);
                        }
                      }}
                    />
                  </FilterField>
                  <FilterField>
                    <Checkbox
                      checked={isStudent}
                      onChange={(_value, checked) => setIsStudent(checked)}
                    >
                      Створено студента
                    </Checkbox>
                  </FilterField>
                </>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
          </>
        }
      >
        <div className="section-card">
          <Table
            className="requests-table"
            wordWrap="keep-all"
            loading={list.loading}
            autoHeight={true}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={rowData => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setUpdateModalObject(rowData);
            }}
            sortColumn={list.sortColumn}
            sortType={list.sortType}
            onSortColumn={(sortColumn, sortType) =>
              list.setSort(sortColumn, sortType || 'desc')
            }
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(column => {
              if (column.display === false) {
                return null;
              }

              const cellClassName = getColumnCellClassName(column);

              return (
                <Column
                  key={column.value}
                  fullText={column.fullText ?? false}
                  sortable={column.value !== 'courses'}
                  width={column.width}
                  fixed={
                    column.fixed ??
                    (column.value === firstVisibleColumnValue
                      ? ('left' as const)
                      : undefined)
                  }
                  resizable={true}
                >
                  <HeaderCell style={{ textAlign: 'center' }}>
                    {column.label}
                  </HeaderCell>
                  {['status'].includes(column.value) ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-nowrap requests-status-cell"
                    >
                      {rowData => {
                        const name = rowData[column.value + 'Name'];
                        return (
                          <StatusBadge tone={statusBadgeTone(name)}>
                            {name || '-'}
                          </StatusBadge>
                        );
                      }}
                    </Cell>
                  ) : ['type'].includes(column.value) ? (
                    <Cell
                      dataKey={column.value}
                      className={cellClassName || 'requests-cell-nowrap'}
                    >
                      {rowData => rowData[column.value + 'Name'] || '-'}
                    </Cell>
                  ) : ['updatedAt', 'createdAt'].includes(column.value) ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-nowrap"
                    >
                      {rowData => formatDateTime(rowData[column.value])}
                    </Cell>
                  ) : column.value === 'courses' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData =>
                        renderCoursesSummary(
                          Array.isArray(rowData[column.value])
                            ? rowData[column.value]
                            : []
                        )
                      }
                    </Cell>
                  ) : column.value === 'data' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData =>
                        truncatedTextCell(rowData[column.value], {
                          maxLength: 96,
                        })
                      }
                    </Cell>
                  ) : column.value === 'course' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData => truncatedTextCell(rowData[column.value])}
                    </Cell>
                  ) : (
                    <Cell dataKey={column.value} className={cellClassName} />
                  )}
                </Column>
              );
            })}

            <Column width={88} fixed="right">
              <HeaderCell>Студент</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px', textAlign: 'center' }}
              >
                {rowData =>
                  rowData.student ? (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Переглянути студента</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        onClick={() => {
                          clickedCellRef.current = true;
                          setStudentModalObject(rowData.student);
                        }}
                      >
                        <img width={18} src="/id-card.svg" alt="Студент" />
                      </Button>
                    </Whisper>
                  ) : (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Створити студента</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        onClick={async () => {
                          clickedCellRef.current = true;
                          const result = await confirm({
                            message: `Ви впевнены що хочете створити студента для анкети ${rowData.firstName} ${rowData.secondName} (${rowData.email})?`,
                            confirmText: 'Створити',
                          });
                          if (!result) {
                            return;
                          }
                          await requestsApi.makeStudent(rowData.id);
                          list.reload();
                        }}
                      >
                        <PlusIcon />
                      </Button>
                    </Whisper>
                  )
                }
              </Cell>
            </Column>

            <Column width={130} fixed="right">
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px', textAlign: 'center' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Додати в чергу</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        onClick={() => {
                          clickedCellRef.current = true;
                          setAddToQueueRequestId(rowData.id);
                        }}
                      >
                        <img width={18} src="/add_to_list.svg" alt="Черга" />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        onClick={() => {
                          clickedCellRef.current = true;
                          setUpdateModalObject(rowData);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          clickedCellRef.current = true;
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити анкету ${rowData.firstName} ${rowData.secondName} (${rowData.email})?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          requestsApi
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            pages={list.totalPages}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>

        {!!studentModalObject && (
          <EditStudentModal
            obj={studentModalObject}
            setObj={setStudentModalObject}
            coursesData={coursesData}
            typesData={studentsTypesData}
          />
        )}
        {addToQueueRequestId.length > 0 && (
          <RequestQueuesModal
            requestId={addToQueueRequestId}
            setRequestId={setAddToQueueRequestId}
            coursesData={coursesData}
          />
        )}
        {!!updateModalObject && (
          <EditRequestModal
            coursesData={coursesData}
            typesData={typesData}
            statusesData={statusesData}
            obj={updateModalObject}
            setObj={setUpdateModalObject}
            callback={() => {
              list.reload();
            }}
          />
        )}
      </PageLayout>
    </>
  );
}
